import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { ConfigProvider, Pagination, Spin } from 'antd'
import debounce from 'lodash/debounce'
import * as React from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { useResizeDetector } from 'react-resize-detector'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PdfViewerProps {
  url: string
  width?: number | string
  height?: number | string
  showPagination?: boolean
  continuousScroll?: boolean
  className?: string
  initialPage?: number
  onLoadSuccess?: (numPages: number) => void
  onLoadError?: (error: Error) => void
}

const ViewerContainer = styled.div<{
  width?: number | string
  height?: number | string
}>`
  width: ${({ width }) =>
    typeof width === 'number' ? `${width}px` : width || '100%'};
  height: ${({ height }) =>
    typeof height === 'number' ? `${height}px` : height || '400px'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`

// Scroll only the PDF content; keep pagination always visible.
const ScrollContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: ${CantileverTheme.colors.error[500]};
  text-align: center;
  padding: 16px;
`

const DocumentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  /* If the PDF content is taller than the container, centering can cause the top
     of the document to be outside the scrollable area. */
  justify-content: flex-start;
`

const PageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
`

const PagesContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px;
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background: ${CantileverTheme.colors.grayscale[100]};
  border-top: 1px solid ${CantileverTheme.colors.grayscale[300]};
`

export const RawPdfViewer: React.FC<PdfViewerProps> = ({
  url,
  width,
  height = 1000,
  showPagination = true,
  continuousScroll = false,
  className,
  initialPage,
  onLoadError,
  onLoadSuccess,
}) => {
  const [numPages, setNumPages] = React.useState<number | null>(null)
  const [currentPage, setCurrentPage] = React.useState<number>(initialPage || 1)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<string | null>(null)

  const { width: detectedWidth, ref } = useResizeDetector<HTMLDivElement>({
    refreshMode: 'debounce',
    refreshRate: 250,
  })

  // Debounce width changes so we don't force PDF canvas re-renders while the layout is changing.
  const [renderWidth, setRenderWidth] = React.useState<number | undefined>(
    detectedWidth ?? undefined
  )

  const debouncedSetRenderWidth = React.useMemo(
    () =>
      debounce((nextWidth: number) => {
        setRenderWidth(nextWidth)
      }, 250),
    []
  )

  React.useEffect(() => {
    if (!detectedWidth) {
      return
    }
    debouncedSetRenderWidth(detectedWidth)
  }, [detectedWidth, debouncedSetRenderWidth])

  React.useEffect(() => {
    return () => {
      debouncedSetRenderWidth.cancel()
    }
  })

  const pageWidth = React.useMemo(() => {
    const fallbackWidth = 600
    if (!renderWidth) {
      return fallbackWidth
    }
    const horizontalPadding = 32
    const calculatedWidth = renderWidth - horizontalPadding
    return calculatedWidth > 0 ? calculatedWidth : fallbackWidth
  }, [renderWidth])

  const onDocumentLoadSuccess = React.useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages)
      setLoading(false)
      setError(null)
      onLoadSuccess?.(numPages)
    },
    [onLoadSuccess]
  )

  const onDocumentLoadError = React.useCallback(
    (error: Error) => {
      setError(error.message)
      setLoading(false)
      onLoadError?.(error)
    },
    [onLoadError]
  )

  React.useEffect(() => {
    setCurrentPage(initialPage || 1)
    setNumPages(null)
    setLoading(true)
    setError(null)
  }, [url, initialPage])

  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  if (showPagination && continuousScroll) {
    console.warn('showPagination and continuousScroll both set simultaneously')
  }

  return (
    <ViewerContainer width={width} height={height} className={className}>
      <ScrollContainer ref={ref}>
        <DocumentContainer>
          {loading && (
            <LoadingContainer>
              <Spin size='large' />
            </LoadingContainer>
          )}
          {error && <ErrorContainer>Error loading PDF: {error}</ErrorContainer>}
          <Document
            key={url}
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <LoadingContainer>
                <Spin size='large' />
              </LoadingContainer>
            }
            error={
              <ErrorContainer>
                Error loading PDF: {error ?? 'Unknown error'}
              </ErrorContainer>
            }>
            {continuousScroll && numPages && numPages > 0 ? (
              <PagesContainer>
                {Array.from({ length: numPages }, (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    renderMode='canvas'
                    width={pageWidth}
                    scale={1.0}
                  />
                ))}
              </PagesContainer>
            ) : (
              <PageContainer>
                <Page
                  key={`page_${currentPage}`}
                  pageNumber={currentPage}
                  renderMode='canvas'
                  width={pageWidth}
                  scale={1.0}
                />
              </PageContainer>
            )}
          </Document>
        </DocumentContainer>
      </ScrollContainer>

      {!continuousScroll && showPagination && numPages && numPages > 1 && (
        <PaginationContainer>
          <ConfigProvider
            theme={{
              token: {
                colorText: '#000',
              },
              components: {
                Pagination: {
                  itemBg: CantileverTheme.colors.grayscale[100],
                  itemLinkBg: CantileverTheme.colors.grayscale[100],
                  itemActiveBg: CantileverTheme.colors.grayscale[300],
                },
              },
            }}>
            <Pagination
              current={currentPage}
              total={numPages}
              pageSize={1}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total) => `Page ${currentPage} of ${total}`}
              onChange={handlePageChange}
              size='small'
            />
          </ConfigProvider>
        </PaginationContainer>
      )}
    </ViewerContainer>
  )
}

RawPdfViewer.displayName = 'RawPdfViewer'

export const PdfViewer = React.memo(RawPdfViewer)
PdfViewer.displayName = 'PdfViewer'
